import { isEmpty } from "lodash";

import urlPaths, { url } from "src/consts/urlPaths";
import { isTagType } from "src/util/tagType";

/** Minimal tree-node shape shared by Tags sidebar and Explore tag navigation. */
export type TagTreeLinkNode = {
  id: string;
  loading?: boolean | undefined;
  isTagType?: boolean | undefined;
  isTagValue?: boolean | undefined;
  tagValue?: Record<string, unknown> | null | undefined;
  children?: unknown[] | null | undefined;
};

export type ExploreTagTreeLinkContext = {
  tagTypesConfig: Record<string, unknown>;
  childrenLoaded: Record<string, boolean>;
  childTagsByNodeId: Record<
    string,
    { tags?: unknown[] | undefined } | undefined
  >;
};

export const tagExploreUrl = (id: string) => url(urlPaths.TAG_EXPLORE, { id });

export const tagDetailsUrl = (id: string) => url(urlPaths.TAG_DETAILS, { id });

/** Eager link targets for the Tags sidebar (children load on chevron expand). */
export const getSidebarTagTreeLinkTo = (
  node: TagTreeLinkNode
): string | null => {
  if (!node?.id) {
    return null;
  }
  if (node.isTagType) {
    return tagExploreUrl(node.id);
  }
  if (!node.isTagValue) {
    return null;
  }
  if (node.loading) {
    return null;
  }
  return tagDetailsUrl(node.id);
};

/** Lazy link targets for Explore tag trees (branch vs leaf depends on load state). */
export const getExploreTagTreeLinkTo = (
  node: TagTreeLinkNode,
  {
    tagTypesConfig,
    childrenLoaded,
    childTagsByNodeId,
  }: ExploreTagTreeLinkContext
): string | null => {
  if (!node?.id || node.loading) {
    return null;
  }
  if (node.isTagType) {
    return tagExploreUrl(node.id);
  }
  if (!node.tagValue && !node.isTagValue) {
    return null;
  }
  if (isTagType(tagTypesConfig, node.id)) {
    return tagExploreUrl(node.id);
  }

  const isLoaded = Boolean(childrenLoaded[node.id]);
  const childTags = childTagsByNodeId[node.id]?.tags;
  const hasTreeChildren = Boolean(node.children?.length);

  if (hasTreeChildren || (isLoaded && !isEmpty(childTags))) {
    return tagExploreUrl(node.id);
  }
  if (isLoaded && isEmpty(childTags)) {
    return tagDetailsUrl(node.id);
  }

  // Provisional explore href until children load — leaf routes redirect to details
  // via TagsExplore useUrlChange; enables copy-link and open-in-new-tab in Explore menu.
  if (node.isTagValue || node.tagValue) {
    return tagExploreUrl(node.id);
  }

  return null;
};
